export type TierName = 'free' | 'basic' | 'pro';

export interface AIModel {
  id: number;
  documentId: string;
  title: string;
  short_description: string;
  is_premium: boolean;
  /** New tier system - free/basic/pro */
  tier?: TierName;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
  user_prompt?: string;
  supports_image?: boolean;
  supports_file?: boolean;
  supports_audio?: boolean;
  supports_image_generation?: boolean;
  supports_pdf?: boolean;
  supports_video?: boolean;
}

/**
 * Get effective tier from model (handles both tier and legacy is_premium)
 */
export function getModelTier(model: AIModel): TierName {
  // Priority 1: Use explicit tier field if set
  if (model.tier && ['free', 'basic', 'pro'].includes(model.tier)) {
    return model.tier;
  }
  // Priority 2: Map legacy is_premium to tier
  if (model.is_premium) {
    return 'basic';
  }
  return 'free';
}

/**
 * Tier display configuration
 */
export const TIER_DISPLAY: Record<TierName, { label: string; color: string; bgColor: string }> = {
  free: { label: 'FREE', color: '#fff', bgColor: '#231ADD' },
  basic: { label: 'BASIC', color: '#000', bgColor: '#FFAB00' },
  pro: { label: 'PRO', color: '#fff', bgColor: '#8B5CF6' },
};

/**
 * Tier access matrix - which user tiers can access which model tiers
 */
const TIER_ACCESS: Record<TierName, TierName[]> = {
  free: ['free'],                    // Free users can only access free models
  basic: ['free', 'basic'],          // Basic users can access free + basic
  pro: ['free', 'basic', 'pro'],     // Pro users can access everything
};

/**
 * Check if a user tier can access a model tier
 * @param modelTier The tier required by the model
 * @param userTier The user's subscription tier
 * @returns true if the user can access the model
 */
export function canAccessModel(modelTier: TierName, userTier: TierName): boolean {
  return TIER_ACCESS[userTier].includes(modelTier);
}
